import { WorkExperienceRole, WorkExperienceBullet, WorkExperienceCompany } from '@applicator/shared'

import { toggleBulletInCompany } from '../../../utils/workExperienceInclusion'

import { toggleSingleBullet, getRoleBulletPoints, isRoleMatchingId, updateBulletText, isBulletMatchingIdForUpdate } from './helpers'

// Helper function to map bullets with toggled bullet
export function mapBulletsWithToggled(bulletPoints: WorkExperienceBullet[], bulletId: string): WorkExperienceBullet[] {
  return bulletPoints.map((bullet) => toggleSingleBullet(bullet, bulletId))
}

// Helper function to get toggled bullets for role
export function getToggledBulletsForRole(role: WorkExperienceRole, bulletId: string): WorkExperienceBullet[] {
  return mapBulletsWithToggled(getRoleBulletPoints(role), bulletId)
}

// Helper function to build role with toggled bullets
export function buildRoleWithToggledBullets(role: WorkExperienceRole, bulletId: string): WorkExperienceRole {
  const toggledBullets = getToggledBulletsForRole(role, bulletId)
  return {
    ...role,
    bulletPoints: toggledBullets,
  }
}

// Helper function to toggle bullet point in role
export function toggleBulletInRole(role: WorkExperienceRole, bulletId: string): WorkExperienceRole {
  return buildRoleWithToggledBullets(role, bulletId)
}

// Helper function to update single role for bullet toggle
export function updateSingleRoleForBulletToggle(
  role: WorkExperienceRole,
  roleId: string,
  bulletId: string
): WorkExperienceRole | null {
  if (!isRoleMatchingId(role, roleId)) {
    return null
  }
  return toggleBulletInRole(role, bulletId)
}

// Helper function to update role or return original
export function updateRoleOrReturnOriginal(
  role: WorkExperienceRole,
  roleId: string,
  bulletId: string
): WorkExperienceRole {
  const updatedRole = updateSingleRoleForBulletToggle(role, roleId, bulletId)
  return updatedRole || role
}

// Helper function to map roles with updated role for bullet toggle
export function mapRolesWithUpdatedRoleForBulletToggle(
  roles: WorkExperienceRole[],
  roleId: string,
  bulletId: string
): WorkExperienceRole[] {
  return roles.map((role) => updateRoleOrReturnOriginal(role, roleId, bulletId))
}

// Helper function to update role in company for bullet toggle
export function updateRoleInCompanyForBulletToggle(
  company: WorkExperienceCompany,
  roleId: string,
  bulletId: string
): WorkExperienceCompany {
  return {
    ...company,
    roles: mapRolesWithUpdatedRoleForBulletToggle(company.roles || [], roleId, bulletId),
  }
}

// Helper function to remove bullet from role
export function removeBulletFromRole(role: WorkExperienceRole, bulletId: string): WorkExperienceRole {
  return {
    ...role,
    bulletPoints: (role.bulletPoints || []).filter((b) => b.id !== bulletId),
  }
}

// Helper function to update single bullet in role
export function updateSingleBulletInRole(bullet: WorkExperienceBullet, bulletId: string, text: string): WorkExperienceBullet {
  if (isBulletMatchingIdForUpdate(bullet, bulletId)) {
    return updateBulletText(bullet, text)
  }
  return bullet
}

// Helper function to map bullets with updated bullet
export function mapBulletsWithUpdatedBullet(
  bulletPoints: WorkExperienceBullet[],
  bulletId: string,
  text: string
): WorkExperienceBullet[] {
  return bulletPoints.map((bullet) => updateSingleBulletInRole(bullet, bulletId, text))
}

// Helper function to get updated bullets for role
export function getUpdatedBulletsForRole(role: WorkExperienceRole, bulletId: string, text: string): WorkExperienceBullet[] {
  return mapBulletsWithUpdatedBullet(getRoleBulletPoints(role), bulletId, text)
}

// Helper function to build role with updated bullets
export function buildRoleWithUpdatedBullets(role: WorkExperienceRole, bulletId: string, text: string): WorkExperienceRole {
  const updatedBullets = getUpdatedBulletsForRole(role, bulletId, text)
  return {
    ...role,
    bulletPoints: updatedBullets,
  }
}

export function updateBulletInRole(role: WorkExperienceRole, bulletId: string, text: string): WorkExperienceRole {
  return buildRoleWithUpdatedBullets(role, bulletId, text)
}

// Helper function to check if role should be updated for bullet update
export function shouldUpdateRoleForBulletUpdate(role: WorkExperienceRole, roleId: string): boolean {
  return isRoleMatchingId(role, roleId)
}

// Helper function to update single role for bullet update
export function updateSingleRoleForBulletUpdate(role: WorkExperienceRole, roleId: string, bulletId: string, text: string): WorkExperienceRole | null {
  if (shouldUpdateRoleForBulletUpdate(role, roleId)) {
    return updateBulletInRole(role, bulletId, text)
  }
  return null
}

// Helper function to update role or return original for bullet update
export function updateRoleOrReturnOriginalForBulletUpdate(role: WorkExperienceRole, roleId: string, bulletId: string, text: string): WorkExperienceRole {
  const updatedRole = updateSingleRoleForBulletUpdate(role, roleId, bulletId, text)
  return updatedRole || role
}

// Helper function to get company roles for update
export function getCompanyRolesForUpdate(company: WorkExperienceCompany): WorkExperienceRole[] {
  return company.roles || []
}

// Helper function to map roles with updated role for bullet update
export function mapRolesWithUpdatedRoleForBulletUpdate(roles: WorkExperienceRole[], roleId: string, bulletId: string, text: string): WorkExperienceRole[] {
  return roles.map((role) => updateRoleOrReturnOriginalForBulletUpdate(role, roleId, bulletId, text))
}

// Helper function to update role in company for bullet update
export function updateRoleInCompanyForBulletUpdate(company: WorkExperienceCompany, roleId: string, bulletId: string, text: string): WorkExperienceCompany {
  const roles = getCompanyRolesForUpdate(company)
  const updatedRoles = mapRolesWithUpdatedRoleForBulletUpdate(roles, roleId, bulletId, text)
  return {
    ...company,
    roles: updatedRoles,
  }
}

// Helper function to update company or return original for bullet update
export function updateCompanyOrReturnOriginalForBulletUpdate(
  company: WorkExperienceCompany,
  companyId: string,
  roleId: string,
  bulletId: string,
  text: string
): WorkExperienceCompany {
  if (company.id === companyId) {
    return updateRoleInCompanyForBulletUpdate(company, roleId, bulletId, text)
  }
  return company
}

// Helper function to map companies with updated bullet
export function mapCompaniesWithUpdatedBullet(
  companiesToMap: WorkExperienceCompany[],
  companyId: string,
  roleId: string,
  bulletId: string,
  text: string
): WorkExperienceCompany[] {
  return companiesToMap.map((company) => updateCompanyOrReturnOriginalForBulletUpdate(company, companyId, roleId, bulletId, text))
}

// Helper function to update company or return original for bullet toggle
export function updateCompanyOrReturnOriginalForBulletToggle(
  company: WorkExperienceCompany,
  companyId: string,
  roleId: string,
  bulletId: string
): WorkExperienceCompany {
  if (company.id === companyId) {
    return updateRoleInCompanyForBulletToggle(company, roleId, bulletId)
  }
  return company
}

// Helper function to map companies with toggled bullet
export function mapCompaniesWithToggledBullet(
  companiesToMap: WorkExperienceCompany[],
  companyId: string,
  roleId: string,
  bulletId: string
): WorkExperienceCompany[] {
  return companiesToMap.map((company) =>
    company.id === companyId ? toggleBulletInCompany(company, roleId, bulletId) : company,
  )
}
